/**
 * src/components/shared/DictateFieldButton.jsx — TICKET-660
 * Mic button that dictates, AI-polishes, and inserts text directly into a
 * field via onInsert(polishedText) -- the caller decides replace vs. append.
 * Sibling to EntityMicButton, not a replacement: EntityMicButton creates
 * breadcrumbs/auto-fills creation forms; this fills an EXISTING field.
 *
 * TICKET-1401 (#2259): red ONLY while recording; while transcribing it shows
 * three animated dots and an accessible "Transcribing…" status instead of a
 * frozen red mic; when a failure kept the audio or text, a Retry button
 * re-runs just the step that failed.
 */

import { Mic, RotateCcw } from 'lucide-react';
import useDictatePolish from '../../hooks/useDictatePolish';
import { useToast } from './ToastProvider';
import { useT } from '../../lib/i18n/I18nContext';

const dotsKeyframes = '@keyframes dictateDot { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0) } 40% { opacity: 1; transform: translateY(-2px) } }';

const roundBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
  borderRadius: '50%',
  border: 'none',
  padding: 0,
};

export default function DictateFieldButton({ kind, onInsert, ariaLabel }) {
  const { t } = useT();
  const { showError } = useToast();
  // TICKET-1190: errors were swallowed (no onError) -- a 400 from UVTP left the
  // mic dead for two months with nothing on screen. Always say it failed.
  const { recording, busy, canRetry, toggle, retry } = useDictatePolish({
    kind,
    onResult: onInsert,
    onError: (e) => showError(`${t('common.dictationFailed', { defaultValue: 'Dictation failed' })}: ${e?.message || ''}`),
  });
  const transcribing = t('dictation.transcribing', { defaultValue: 'Transcribing…' });

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <button
        type="button"
        data-testid="dictate-field-btn"
        data-state={recording ? 'recording' : busy ? 'processing' : 'idle'}
        onClick={toggle}
        aria-label={busy ? transcribing : (ariaLabel || 'Dictate')}
        aria-busy={busy || undefined}
        disabled={busy}
        style={{
          ...roundBtn,
          background: recording ? 'var(--bad)' : 'var(--navy-surface)',
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        {busy ? (
          <span data-testid="dictate-field-dots" aria-hidden="true" style={{ display: 'inline-flex', gap: 3 }}>
            <style>{dotsKeyframes}</style>
            {[0, 1, 2].map((i) => (
              <span key={i} style={{
                display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
                background: 'var(--on-navy)',
                animation: `dictateDot 1s ease-in-out ${i * 0.15}s infinite`,
              }} />
            ))}
          </span>
        ) : (
          <Mic size={18} color="var(--on-navy)" strokeWidth={1.5} />
        )}
      </button>
      {busy && (
        <span data-testid="dictate-field-status" role="status" aria-live="polite" style={{ fontSize: 12, color: 'var(--t2)' }}>
          {transcribing}
        </span>
      )}
      {canRetry && !busy && !recording && (
        <button
          type="button"
          data-testid="dictate-field-retry"
          onClick={retry}
          aria-label={t('dictation.retry', { defaultValue: 'Retry' })}
          style={{ ...roundBtn, background: 'var(--glass)', cursor: 'pointer' }}
        >
          <RotateCcw size={16} color="var(--t2)" strokeWidth={1.5} />
        </button>
      )}
    </span>
  );
}
